/**
 * N3 · Competência 6 (Estatística) · **eixo cognitivo III** · task-model H3.33.K01
 *
 *   "Mexer numa coluna até a média do conjunto ser a pedida."
 *
 * O contrário do item de média: ali os dados estavam dados e a média era a incógnita;
 * aqui a MÉDIA está dada e um dado é a incógnita. É a habilidade H3.33 quase ao pé da
 * letra — *modificar dados de uma distribuição para que ela assuma características de
 * tendência central pré-determinadas* — e é um gesto que o papel não tem: arrastar uma
 * coluna e ver a média perseguir o alvo.
 *
 * POR QUE UMA COLUNA SÓ SE MEXE. Com todas livres, existe uma saída sem pensamento
 * nenhum: pôr todas na altura da média pedida. Travando as outras, a resposta é única e
 * sai de uma conta — quanto falta ao total, e não à média.
 *
 * O ERRO QUE DÁ NOME AO ITEM é mover a coluna pelo tanto que a MÉDIA precisa mudar. É
 * quase irresistível: a média tem de subir 3, então a coluna sobe 3. Mas a mudança se
 * reparte entre as `n` colunas, e a coluna precisa subir `n` vezes isso. Por isso o
 * enunciado diz qual é a média de hoje: sem esse número na tela, o erro não teria como
 * se formar, e o item mediria menos.
 *
 * A figura é o mesmo `barras()` dos outros itens de coluna, desenhada com a coluna a
 * mexer em zero — `barras()` não desenha coluna de valor zero —, e essa coluna volta
 * como a camada arrastável.
 */

import type { Rng } from '../../core/rng.ts';
import { formatBR } from '../../core/num.ts';
import type { Vec2 } from '../../geom/vec.ts';
import { barras } from '../../render/chart.ts';
import type { Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  EST_ALVO_NA_SOMA,
  EST_COLUNA_VIRA_A_MEDIA,
  EST_DIVIDE_SEM_A_COLUNA,
  EST_MOVEU_PELA_DIFERENCA,
  EST_SENTIDO_INVERTIDO,
} from '../../schema/errors.ts';
import type { DragTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

const W = 320;
const H = 180;
const Y_TOP = 40;
const GRADE = 2;
/** Unidades de desenho por unidade de valor. */
const POR_UNIDADE = H / Y_TOP;

interface Contexto {
  readonly texto: string;
  readonly titulo: string;
  readonly categoria: string;
  readonly eixo: string;
  readonly unidade: string;
  readonly nomes: readonly string[];
  /**
   * Como a prosa se refere a uma coluna. O rótulo do gráfico é curto ("2ª", "Maio"), e
   * colado em "a coluna de" dava "a coluna de 2ª" — 2ª o quê? — e "a coluna de Maio",
   * com maiúscula no meio da frase.
   */
  readonly referir: (rotulo: string) => string;
}

const CONTEXTOS: readonly Contexto[] = [
  {
    texto: 'O gráfico mostra quantos pontos um time fez em cada rodada de um campeonato escolar.',
    titulo: 'Pontos por rodada',
    categoria: 'Rodada',
    eixo: 'Pontos',
    unidade: 'pontos',
    nomes: ['1ª', '2ª', '3ª', '4ª', '5ª'],
    referir: (r) => `da ${r} rodada`,
  },
  {
    texto: 'Uma oficina anotou quantas bicicletas consertou em cada dia da semana.',
    titulo: 'Bicicletas consertadas',
    categoria: 'Dia',
    eixo: 'Bicicletas',
    unidade: 'bicicletas',
    nomes: ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'],
    referir: (r) => `de ${r.toLowerCase()}-feira`,
  },
  {
    texto: 'Uma turma registrou quantos livros leu em cada mês do semestre.',
    titulo: 'Livros lidos por mês',
    categoria: 'Mês',
    eixo: 'Livros',
    unidade: 'livros',
    nomes: ['Março', 'Abril', 'Maio', 'Junho', 'Julho'],
    referir: (r) => `de ${r.toLowerCase()}`,
  },
];

export interface Params {
  readonly contexto: number;
  readonly n: number;
  /** Os valores de hoje, na ordem das categorias. */
  readonly valores: readonly number[];
  /** Qual coluna o aluno pode mexer. */
  readonly alvo: number;
  /** A média pedida. Inteira: uma média "3,25" pedida vira um item de aritmética. */
  readonly media: number;
}

/** A altura certa da coluna que se mexe: o que falta ao TOTAL, não à média. */
const altura = (p: Params): number => p.n * p.media - soma(p, true);

/** A soma das colunas; `semAlvo` deixa de fora a que está sendo mexida. */
function soma(p: Params, semAlvo = false): number {
  return p.valores.reduce((s, v, i) => s + (semAlvo && i === p.alvo ? 0 : v), 0);
}

const mediaDeHoje = (p: Params): number => soma(p) / p.n;

function drawParams(rng: Rng): Params {
  const n = rng.pick([4, 5]);
  // Valores pares de 6 a 34: cada um cai numa linha da grade, e sobra teto e chão para
  // a coluna a mexer ir para os dois lados.
  const pool = Array.from({ length: 15 }, (_v, i) => (i + 3) * GRADE);

  let melhor: Params | null = null;
  for (let tentativa = 0; tentativa < 40 && !melhor; tentativa++) {
    const valores = rng.shuffle(pool).slice(0, n);
    const alvo = rng.int(0, n - 1);
    const base: Params = { contexto: 0, n, valores, alvo, media: 0 };
    const outras = soma(base, true);
    // As médias que dão uma altura desenhável, longe da atual e do teto.
    const medias: number[] = [];
    for (let m = 8; m <= 30; m++) {
      const h = n * m - outras;
      if (h % GRADE !== 0) continue;
      if (h < 4 || h > Y_TOP - 4) continue;
      if (Math.abs(h - (valores[alvo] as number)) < 2 * GRADE) continue;
      medias.push(m);
    }
    if (medias.length > 0) melhor = { ...base, media: rng.pick(medias) };
  }

  const p = melhor ?? {
    contexto: 0,
    n,
    valores: rng.shuffle(pool).slice(0, n),
    alvo: 0,
    media: 12,
  };
  return { ...p, contexto: rng.int(0, CONTEXTOS.length - 1) };
}

const slot = (n: number): number => W / n;
const xDe = (i: number, n: number): number => i * slot(n) + slot(n) / 2;

/** Uma casa só quando a conta pede: média inteira não vira "19,0". */
const n1 = (x: number): string => formatBR(Math.round(x * 10) / 10, Number.isInteger(x) ? 0 : 1);
const n0 = (x: number): string => formatBR(x, 0);

function alvoDe(p: Params): DragTarget {
  const nomes = (CONTEXTOS[p.contexto] as Contexto).nomes;
  const nome = nomes[p.alvo] as string;
  const daColuna = (CONTEXTOS[p.contexto] as Contexto).referir(nome);
  const v0 = p.valores[p.alvo] as number;
  const certa = altura(p);
  const outras = soma(p, true);
  const h = (pos: readonly Vec2[]): number => Math.round((pos[0] as Vec2).y / POR_UNIDADE);
  /** Dentro de meio passo do arrasto: é a mesma posição na malha. */
  const perto = (a: number, b: number): boolean => Math.abs(a - b) <= GRADE / 2;

  return {
    describe: [
      txt(
        `Arraste o topo da coluna ${daColuna} até a média das ${p.n} colunas ser ${n0(p.media)}.`,
      ),
    ],
    correto: (pos) => h(pos) === certa,
    diagnostics: [
      {
        // O erro que dá nome ao item: mover a coluna pelo tanto que a MÉDIA muda.
        error: EST_MOVEU_PELA_DIFERENCA,
        test: (pos) => perto(h(pos), v0 + (p.media - mediaDeHoje(p))),
      },
      { error: EST_COLUNA_VIRA_A_MEDIA, test: (pos) => perto(h(pos), p.media) },
      { error: EST_ALVO_NA_SOMA, test: (pos) => perto(h(pos), p.media - outras) },
      {
        error: EST_DIVIDE_SEM_A_COLUNA,
        test: (pos) => perto(h(pos), (p.n - 1) * p.media - outras),
      },
      { error: EST_SENTIDO_INVERTIDO, test: (pos) => perto(h(pos), 2 * v0 - certa) },
    ],
    partialCredit: [
      {
        id: 'sentido',
        label: certa > v0 ? `Subiu a coluna ${daColuna}` : `Desceu a coluna ${daColuna}`,
        weight: 0.3,
        // Saber PARA ONDE a média precisa andar já é metade do raciocínio, e é o que
        // separa quem entendeu o problema de quem chutou.
        test: (pos) => (certa > v0 ? h(pos) > v0 : h(pos) < v0),
      },
      {
        id: 'altura',
        label: `Coluna ${daColuna} em ${n0(certa)}`,
        weight: 0.7,
        test: (pos) => h(pos) === certa,
      },
    ],
    analyze(pos) {
      const hh = h(pos);
      const out: Inline[] = [
        strong(`${nome}: `),
        txt(hh === certa ? `${n0(hh)}, certa.` : `você deixou em ${n0(hh)}.`),
        br,
        txt(`Com esse valor, a soma das ${p.n} colunas fica ${n0(outras + hh)} e a média, `),
        strong(`${n1((outras + hh) / p.n)}`),
        txt(hh === certa ? '.' : ` — e a média pedida é ${n0(p.media)}.`),
      ];
      return out;
    },
    reviewShapes(pos) {
      if (h(pos) === certa) return [];
      const w = slot(p.n) * 0.56;
      return [
        {
          t: 'rect',
          at: { x: xDe(p.alvo, p.n) - w / 2, y: 0, w, h: certa * POR_UNIDADE },
          role: 'construction',
        },
      ] as Shape[];
    },
    reviewLegend: [txt('Contorno tracejado: a altura que deixa a média no valor pedido.')],
  };
}

export const mediaAlvo: ItemTemplate<Params, null> = {
  id: templateId('h3-33-K01-media-alvo'),
  version: 1,
  taskModel: taskModelCode('H3.33.K01'),
  skills: [skillCode('H3.33')],
  axis: 'III',
  level: 'N3',
  title: 'Mexer numa coluna até a média ser a pedida',
  responseModel: 'GPCM',
  difficultySources: ['steps.2', 'inverse-problem'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.n === 5) fontes.push('steps.3plus');
    // Média de hoje fracionária: a comparação "de quanto a média precisa andar" deixa
    // de ser uma subtração de cabeça.
    if (soma(p) % p.n !== 0) fontes.push('non-round-numbers');
    return fontes;
  },
  validate(_b, p) {
    const h = altura(p);
    if (h % GRADE !== 0) return 'a altura certa não cai numa linha da grade';
    if (h < 4 || h > Y_TOP - 4) return 'a altura certa não cabe no gráfico';
    if (Math.abs(h - (p.valores[p.alvo] as number)) < 2 * GRADE)
      return 'a coluna quase não precisa se mexer';
    return null;
  },
  build(p) {
    const ctx = CONTEXTOS[p.contexto] as Contexto;
    const nomes = ctx.nomes.slice(0, p.n);
    const nome = nomes[p.alvo] as string;
    const daColuna = ctx.referir(nome);
    const v0 = p.valores[p.alvo] as number;
    const certa = altura(p);

    // A coluna que se mexe entra em zero: `barras()` não desenha coluna de valor zero, e
    // ela volta como a camada arrastável, por cima.
    const base = barras({
      categorias: nomes,
      valores: p.valores.map((v, i) => (i === p.alvo ? 0 : v)),
      yTop: Y_TOP,
      yStep: 10,
      gridStep: GRADE,
      xLabel: ctx.categoria,
      yLabel: ctx.eixo,
      title: ctx.titulo,
      desc: '',
    });

    const descricao =
      `Gráfico de colunas "${ctx.titulo}", com o eixo vertical de 0 a ${Y_TOP}, números de 10 em 10 e linhas de grade ` +
      `de ${GRADE} em ${GRADE}. As colunas são ${nomes
        .map(
          (nm, i) =>
            `${nm}, ${i === p.alvo ? `${n0(v0)} (a que se pode mexer)` : n0(p.valores[i] as number)}`,
        )
        .join('; ')}. O topo da coluna ${daColuna} pode ser arrastado para cima ou para baixo.`;

    return {
      stem: [
        txt(`${ctx.texto} Hoje a média das ${p.n} colunas é `),
        strong(n1(mediaDeHoje(p))),
        txt('.'),
      ],
      comando: [
        txt(
          `Mexa só na coluna ${daColuna}: arraste o topo dela até a média das ${p.n} colunas ser `,
        ),
        strong(`${n0(p.media)} ${ctx.unidade}`),
        txt('.'),
      ],
      stimuli: [],
      interaction: {
        kind: 'drag-points',
        descricao,
        backdrop: base.shapes,
        viewBox: base.viewBox,
        pontos: [{ nome, inicial: { x: xDe(p.alvo, p.n), y: v0 * POR_UNIDADE } }],
        ligacoes: [],
        passo: GRADE * POR_UNIDADE,
        unidade: POR_UNIDADE,
        trava: 'y',
        desenho: 'barras',
        base: 0,
        larguraBarra: slot(p.n) * 0.56,
        limites: { x: 0, y: 0, w: W, h: Y_TOP * POR_UNIDADE },
        alvo: alvoDe(p),
      },
      dicas: [
        [
          txt('A média não muda o que ela quer sozinha: quem muda é a '),
          strong('soma'),
          txt(`. Com ${p.n} colunas, média ${n0(p.media)} quer dizer soma ${n0(p.n * p.media)}.`),
        ],
        [
          txt(`As outras ${p.n - 1} colunas somam ${n0(soma(p, true))} e não se mexem. `),
          txt('Então a coluna que falta precisa completar o que sobra dessa soma.'),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `Média ${n0(p.media)} em ${p.n} colunas quer dizer soma ${n0(p.n)} × ${n0(p.media)} = ${n0(p.n * p.media)}. `,
        ),
        txt(
          `As outras ${p.n - 1} colunas somam ${n0(soma(p, true))}, então a coluna ${daColuna} precisa valer `,
        ),
        txt(`${n0(p.n * p.media)} − ${n0(soma(p, true))} = ${n0(certa)}.`),
        br,
        txt(
          `Repare no tamanho do movimento: a média vai de ${n1(mediaDeHoje(p))} a ${n0(p.media)}, uma diferença de `,
        ),
        txt(`${n1(Math.abs(p.media - mediaDeHoje(p)))}; mas a coluna anda `),
        txt(
          `${n0(Math.abs(certa - v0))}, que é ${n0(p.n)} vezes isso — a mudança de uma coluna se reparte entre as ${p.n}.`,
        ),
      ],
      declared: 'media',
    };
  },
};
